import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    X,
    Save,
    Loader2,
    UploadCloud,
    ImageIcon,
    Sparkles,
    Trash2,
    Eye,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { crud, absoluteUrl, sliderGenerateBackground } from "../lib/api";
import { PANEL_INPUT, PANEL_MODAL_SHELL } from "../lib/panelFormStyles";

const EXTRA_IMAGE_FIELDS = [
    { key: "resim_1", label: "Ek görsel 1" },
    { key: "resim_2", label: "Ek görsel 2" },
    { key: "resim_3", label: "Ek görsel 3" },
    { key: "resim_4", label: "Ek görsel 4" },
];

function blobToFile(blob, filename) {
    return new File([blob], filename, {
        type: blob.type || "image/png",
        lastModified: Date.now(),
    });
}

function ImageSlot({
    label,
    previewUrl,
    busy,
    onPick,
    onClear,
    onGenerate,
    generateLabel,
    tall = false,
    hint,
}) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    function handleDrop(e) {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file && file.type.startsWith("image/")) onPick(file);
    }

    return (
        <div className="rounded-xl border border-white/10 bg-[#0B0F19]/60 overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
                <span className="text-xs font-medium text-slate-200">{label}</span>
                <div className="flex items-center gap-2">
                    {onGenerate ? (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={onGenerate}
                            className="inline-flex items-center gap-1 text-[11px] text-[#D4AF37] hover:underline disabled:opacity-50"
                        >
                            <Sparkles className="h-3 w-3" />
                            {generateLabel || "Otomatik üret"}
                        </button>
                    ) : null}
                    {previewUrl ? (
                        <button
                            type="button"
                            onClick={onClear}
                            className="inline-flex items-center gap-1 text-[11px] text-red-300 hover:underline"
                        >
                            <Trash2 className="h-3 w-3" />
                            Kaldır
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="text-[11px] text-slate-300 hover:text-white"
                        onClick={() => inputRef.current?.click()}
                    >
                        Yükle
                    </button>
                </div>
            </div>

            <div
                className={`relative ${tall ? "aspect-[16/9]" : "aspect-[16/10]"} bg-[#050810]`}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
            >
                {previewUrl ? (
                    <img
                        src={previewUrl}
                        alt={label}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                ) : (
                    <div
                        className={`absolute inset-0 flex flex-col items-center justify-center text-slate-500 ${
                            dragOver ? "bg-[#D4AF37]/10 ring-2 ring-inset ring-[#D4AF37]/40" : ""
                        }`}
                    >
                        <UploadCloud className="h-8 w-8 mb-2 opacity-60" />
                        <span className="text-xs">Sürükle-bırak veya yükle</span>
                        {hint ? (
                            <span className="mt-1 px-4 text-center text-[10px] text-slate-600">
                                {hint}
                            </span>
                        ) : null}
                    </div>
                )}
                {busy ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <Loader2 className="h-7 w-7 animate-spin text-[#D4AF37]" />
                    </div>
                ) : null}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0] || null)}
            />
        </div>
    );
}

export default function SliderFormModal({ open, title, initial, onClose, onSaved }) {
    const isEdit = !!initial?.id;
    const [baslik, setBaslik] = useState("");
    const [altBaslik, setAltBaslik] = useState("");
    const [link, setLink] = useState("");
    const [siralama, setSiralama] = useState("0");
    const [aktif, setAktif] = useState(true);
    const [files, setFiles] = useState({});
    const [cleared, setCleared] = useState({});
    const [busy, setBusy] = useState(false);
    const [genBusy, setGenBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [showExtra, setShowExtra] = useState(false);
    const [autoGenEnabled, setAutoGenEnabled] = useState(true);

    useEffect(() => {
        if (!open) return;
        setErr(null);
        setFiles({});
        setCleared({});
        setBaslik(initial?.baslik || "");
        setAltBaslik(initial?.alt_baslik || "");
        setLink(initial?.link || "");
        setSiralama(String(initial?.siralama ?? 0));
        setAktif(initial?.aktif !== false);
        setShowExtra(false);
        setAutoGenEnabled(true);
    }, [open, initial]);

    const previews = useMemo(() => {
        const out = {};
        const keys = ["arkaplan", ...EXTRA_IMAGE_FIELDS.map((f) => f.key)];
        for (const key of keys) {
            if (cleared[key]) continue;
            const picked = files[key];
            if (picked instanceof File) {
                out[key] = URL.createObjectURL(picked);
            } else if (initial?.[key]) {
                out[key] = absoluteUrl(initial[key]);
            }
        }
        return out;
    }, [files, initial, cleared]);

    useEffect(() => {
        return () => {
            Object.values(previews).forEach((url) => {
                if (url?.startsWith?.("blob:")) URL.revokeObjectURL(url);
            });
        };
    }, [previews]);

    const heroPreview = previews.arkaplan || null;

    const generateBackground = useCallback(
        async (silent = false) => {
            const t = baslik.trim();
            if (!t) {
                if (!silent) toast.error("Önce başlık yazın.");
                return null;
            }
            setGenBusy(true);
            try {
                const seed = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                const blob = await sliderGenerateBackground(t, altBaslik.trim(), seed);
                const file = blobToFile(blob, `slider-${Date.now()}.png`);
                setFiles((prev) => ({ ...prev, arkaplan: file }));
                setCleared((prev) => {
                    const next = { ...prev };
                    delete next.arkaplan;
                    return next;
                });
                if (!silent) {
                    toast.success("Premium arka plan hazır — beğenmezseniz tekrar üretin, her seferinde farklı tasarım.");
                }
                return file;
            } catch (e) {
                const msg = e?.response?.data?.detail || e?.message || "Arka plan üretilemedi.";
                if (!silent) toast.error(msg);
                return null;
            } finally {
                setGenBusy(false);
            }
        },
        [baslik, altBaslik],
    );

    if (!open) return null;

    function pickFile(key, file) {
        if (!file) return;
        setFiles((prev) => ({ ...prev, [key]: file }));
        setCleared((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }

    function clearFile(key) {
        setFiles((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setCleared((prev) => ({ ...prev, [key]: true }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setErr(null);

        if (!baslik.trim()) {
            setErr("Başlık zorunludur.");
            return;
        }

        const meta = {
            baslik: baslik.trim(),
            alt_baslik: altBaslik.trim(),
            link: link.trim() || "",
            siralama: Number(siralama) || 0,
            aktif,
        };

        setBusy(true);
        try {
            const payload = { ...meta, ...files };
            const hasArkaplan =
                files.arkaplan instanceof File ||
                (!cleared.arkaplan && initial?.arkaplan);

            if (!hasArkaplan && autoGenEnabled) {
                const generated = await generateBackground(true);
                if (generated) payload.arkaplan = generated;
            }

            if (isEdit) {
                const hasFiles = Object.values(files).some((f) => f instanceof File);
                if (hasFiles) {
                    await crud.uploadPatch("slider", initial.id, payload);
                } else {
                    await crud.update("slider", initial.id, meta);
                }
                toast.success("Slider güncellendi — anasayfa birkaç saniye içinde güncellenir.");
            } else {
                await crud.upload("slider", payload);
                toast.success("Slider oluşturuldu — anasayfa birkaç saniye içinde güncellenir.");
            }
            onSaved?.();
        } catch (e) {
            const data = e?.response?.data;
            let msg;
            if (data && typeof data === "object") {
                msg = Object.entries(data)
                    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
                    .join(" · ");
            } else {
                msg = data?.detail || e?.message || "Kayıt başarısız.";
            }
            setErr(msg);
        } finally {
            setBusy(false);
        }
    }

    return createPortal(
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm">
            <div className={`${PANEL_MODAL_SHELL} max-w-5xl w-full max-h-[94vh]`}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div>
                        <h2 className="text-lg font-semibold text-white">{title}</h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Başlığa göre otomatik arka plan · sürükle-bırak yükleme · canlı önizleme
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
                    <div className="overflow-y-auto px-5 py-4 space-y-5 flex-1">
                        {err ? (
                            <p className="text-sm text-red-300 border border-red-500/30 rounded-md px-3 py-2 bg-red-500/10">
                                {err}
                            </p>
                        ) : null}

                        <div className="rounded-xl border border-[#D4AF37]/25 bg-[#0B0F19]/80 overflow-hidden">
                            <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 text-xs text-slate-300">
                                <Eye className="h-3.5 w-3.5 text-[#D4AF37]" />
                                Anasayfa slider önizlemesi
                            </div>
                            <div className="relative aspect-[16/7] min-h-[180px] bg-brand-navy">
                                {heroPreview ? (
                                    <img
                                        src={heroPreview}
                                        alt="Slider önizleme"
                                        className="absolute inset-0 h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="absolute inset-0 bg-gradient-to-br from-[#1A222E] via-[#243044] to-[#0B0F19]" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19]/90 via-[#0B0F19]/55 to-transparent" />
                                <div className="absolute inset-0 flex items-center px-6 sm:px-10">
                                    <div className="max-w-lg">
                                        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] uppercase tracking-widest text-white/85">
                                            MBS Hukuk Danışmanlık
                                        </span>
                                        <h3 className="mt-4 font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">
                                            {baslik.trim() || "Başlık burada görünür"}
                                        </h3>
                                        {altBaslik.trim() ? (
                                            <p className="mt-3 text-sm sm:text-base text-white/80 leading-relaxed line-clamp-3">
                                                {altBaslik.trim()}
                                            </p>
                                        ) : (
                                            <p className="mt-3 text-sm text-white/45 italic">
                                                Alt başlık (isteğe bağlı)
                                            </p>
                                        )}
                                        <div className="mt-5 flex flex-wrap gap-2">
                                            <span className="inline-flex items-center rounded-md bg-[#D4AF37] px-3 py-1.5 text-xs font-semibold text-[#0B0F19]">
                                                Detaylı bilgi
                                            </span>
                                            <span className="inline-flex items-center rounded-md border border-white/30 px-3 py-1.5 text-xs text-white/90">
                                                Online başvuru
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid gap-4 lg:grid-cols-2">
                            <label className="block lg:col-span-2">
                                <span className="text-xs text-slate-400 mb-1 block">Başlık *</span>
                                <input
                                    className={PANEL_INPUT}
                                    value={baslik}
                                    onChange={(e) => setBaslik(e.target.value)}
                                    placeholder="Örn. İstanbul Değer Kaybı Avukatı"
                                    required
                                />
                            </label>
                            <label className="block lg:col-span-2">
                                <span className="text-xs text-slate-400 mb-1 block">Alt başlık</span>
                                <textarea
                                    className={`${PANEL_INPUT} min-h-[88px]`}
                                    value={altBaslik}
                                    onChange={(e) => setAltBaslik(e.target.value)}
                                    placeholder="Kısa açıklama — slider metninde görünür"
                                />
                            </label>
                            <label className="block lg:col-span-2">
                                <span className="text-xs text-slate-400 mb-1 block">Link (CTA)</span>
                                <input
                                    className={PANEL_INPUT}
                                    type="url"
                                    placeholder="https://mbshukuk.com/deger-kaybi/"
                                    value={link}
                                    onChange={(e) => setLink(e.target.value)}
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs text-slate-400 mb-1 block">Sıralama</span>
                                <input
                                    className={PANEL_INPUT}
                                    type="number"
                                    min="0"
                                    value={siralama}
                                    onChange={(e) => setSiralama(e.target.value)}
                                />
                            </label>
                            <div className="flex flex-col gap-3 pt-1">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={aktif}
                                        onChange={(e) => setAktif(e.target.checked)}
                                        className="rounded border-white/20"
                                    />
                                    <span className="text-sm text-slate-200">Yayında</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={autoGenEnabled}
                                        onChange={(e) => setAutoGenEnabled(e.target.checked)}
                                        className="rounded border-white/20"
                                    />
                                    <span className="text-sm text-slate-200">
                                        Görsel yoksa kayıtta otomatik üret
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className="border-t border-white/10 pt-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-medium text-slate-200 flex items-center gap-2">
                                    <ImageIcon className="h-4 w-4 text-[#D4AF37]" />
                                    Ana arka plan
                                </p>
                            </div>
                            <ImageSlot
                                label="Hero arka plan (1920×1080)"
                                previewUrl={previews.arkaplan}
                                busy={genBusy}
                                onPick={(f) => pickFile("arkaplan", f)}
                                onClear={() => clearFile("arkaplan")}
                                onGenerate={() => generateBackground(false)}
                                generateLabel="Yeni varyant üret"
                                tall
                                hint="Sinematik foto + renk düzenleme · 8 kompozisyon · her tıklamada benzersiz varyant"
                            />
                        </div>

                        <div className="border-t border-white/10 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowExtra((v) => !v)}
                                className="flex w-full items-center justify-between py-2 text-sm text-slate-300 hover:text-white"
                            >
                                <span>Ek görseller (aynı metinle ayrı slaytlar)</span>
                                {showExtra ? (
                                    <ChevronUp className="h-4 w-4" />
                                ) : (
                                    <ChevronDown className="h-4 w-4" />
                                )}
                            </button>
                            {showExtra ? (
                                <div className="grid gap-3 sm:grid-cols-2 pt-2">
                                    {EXTRA_IMAGE_FIELDS.map(({ key, label }) => (
                                        <ImageSlot
                                            key={key}
                                            label={label}
                                            previewUrl={previews[key]}
                                            busy={false}
                                            onPick={(f) => pickFile(key, f)}
                                            onClear={() => clearFile(key)}
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm rounded-md border border-white/15 text-slate-300 hover:bg-white/5"
                        >
                            İptal
                        </button>
                        <button
                            type="button"
                            disabled={genBusy || !baslik.trim()}
                            onClick={() => generateBackground(false)}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50"
                        >
                            {genBusy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            Arka plan üret (yeni)
                        </button>
                        <button
                            type="submit"
                            disabled={busy}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-[#D4AF37] text-[#0B0F19] font-medium hover:bg-[#c4a030] disabled:opacity-60"
                        >
                            {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4" />
                            )}
                            Kaydet
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body,
    );
}
